/**
 * Cross-cutting alert primitives for new vendor orders.
 *
 * Browsers block audio autoplay until the user has interacted with the page,
 * and require explicit permission for notifications. The helpers below try
 * gracefully and silently no-op if blocked.
 */

import { partnerAppName } from './tenant'

const STORAGE_DISMISSED_KEY = 'cravo-notification-prompt-dismissed'
const STORAGE_SOUND_ENABLED_KEY = 'cravo-order-sound-enabled'

let audioCtx: AudioContext | null = null

function getAudioCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null
  if (audioCtx) return audioCtx
  const Ctor = (window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext)
  if (!Ctor) return null
  try {
    audioCtx = new Ctor()
    return audioCtx
  } catch {
    return null
  }
}

/**
 * Play a short two-tone bell using Web Audio (no asset needed).
 * Resumes the AudioContext on user gesture if suspended.
 */
export function playNewOrderSound(): void {
  if (!isSoundEnabled()) return
  const ctx = getAudioCtx()
  if (!ctx) return

  if (ctx.state === 'suspended') {
    void ctx.resume().catch(() => {})
  }

  const now = ctx.currentTime
  const tones = [
    { freq: 880, start: 0, duration: 0.18 },
    { freq: 1320, start: 0.22, duration: 0.28 },
  ]

  for (const tone of tones) {
    const oscillator = ctx.createOscillator()
    const gain = ctx.createGain()
    oscillator.type = 'sine'
    oscillator.frequency.value = tone.freq
    oscillator.connect(gain)
    gain.connect(ctx.destination)

    const startAt = now + tone.start
    const stopAt = startAt + tone.duration
    gain.gain.setValueAtTime(0.0001, startAt)
    gain.gain.exponentialRampToValueAtTime(0.4, startAt + 0.02)
    gain.gain.exponentialRampToValueAtTime(0.0001, stopAt)
    oscillator.start(startAt)
    oscillator.stop(stopAt + 0.05)
  }
}

export function isNotificationApiAvailable(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window
}

export function getNotificationPermission(): NotificationPermission | 'unsupported' {
  if (!isNotificationApiAvailable()) return 'unsupported'
  return Notification.permission
}

export async function requestNotificationPermission(): Promise<NotificationPermission | 'unsupported'> {
  if (!isNotificationApiAvailable()) return 'unsupported'
  try {
    const result = await Notification.requestPermission()
    return result
  } catch {
    return 'denied'
  }
}

export function showOrderNotification(count: number): void {
  if (!isNotificationApiAvailable()) return
  if (Notification.permission !== 'granted') return
  if (typeof document !== 'undefined' && document.visibilityState === 'visible') return

  try {
    const title = count === 1 ? 'New order received' : `${count} new orders received`
    const body = `Open ${partnerAppName()} to accept the request.`
    const notification = new Notification(title, {
      body,
      tag: 'cravo-new-order',
      icon: '/icon.png',
      badge: '/icon.png',
      requireInteraction: false,
    })
    notification.onclick = () => {
      window.focus()
      notification.close()
    }
  } catch {
    // Some browsers throw if a notification with the same tag exists.
  }
}

let originalTitle: string | null = null
let titleInterval: ReturnType<typeof setInterval> | null = null
let titlePulseCount: number | null = null

export function flashTitleForNewOrders(count: number): void {
  if (typeof document === 'undefined') return
  if (document.visibilityState === 'visible') return

  const newTitle = count === 1 ? '🔔 1 new order' : `🔔 ${count} new orders`

  if (titleInterval) {
    clearInterval(titleInterval)
    titleInterval = null
  }

  if (originalTitle == null) {
    originalTitle = document.title
  }
  titlePulseCount = count

  let toggle = false
  titleInterval = setInterval(() => {
    if (document.visibilityState === 'visible') {
      stopTitleFlash()
      return
    }
    document.title = toggle ? originalTitle ?? document.title : newTitle
    toggle = !toggle
  }, 1500)
}

export function stopTitleFlash(): void {
  if (titleInterval) {
    clearInterval(titleInterval)
    titleInterval = null
  }
  if (originalTitle != null) {
    document.title = originalTitle
    originalTitle = null
  }
  titlePulseCount = null
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && titlePulseCount != null) {
      stopTitleFlash()
    }
  })
}

// ─── User preferences ─────────────────────────────────────────────

export function isSoundEnabled(): boolean {
  if (typeof localStorage === 'undefined') return true
  const value = localStorage.getItem(STORAGE_SOUND_ENABLED_KEY)
  return value !== 'false'
}

export function setSoundEnabled(enabled: boolean): void {
  if (typeof localStorage === 'undefined') return
  localStorage.setItem(STORAGE_SOUND_ENABLED_KEY, enabled ? 'true' : 'false')
}

export function isPermissionPromptDismissed(): boolean {
  if (typeof localStorage === 'undefined') return false
  return localStorage.getItem(STORAGE_DISMISSED_KEY) === 'true'
}

export function dismissPermissionPrompt(): void {
  if (typeof localStorage === 'undefined') return
  localStorage.setItem(STORAGE_DISMISSED_KEY, 'true')
}

/**
 * Trigger every alert channel for a positive delta in pending orders.
 */
export function notifyNewOrders(delta: number): void {
  if (delta <= 0) return
  playNewOrderSound()
  showOrderNotification(delta)
  flashTitleForNewOrders(delta)
}
